import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import MapPicker from "@/components/MapPicker";
import { api } from "@/lib/api";

const EMPTY = {
  label: "Home", name: "", phone: "",
  line1: "", line2: "", city: "", state: "", pincode: "",
  country: "India", latitude: null, longitude: null,
  place_name: "", is_default: false,
};

export default function AddressForm({ initial = {}, onSaved, onCancel, defaultName = "" }) {
  const [form, setForm] = useState({ ...EMPTY, name: defaultName, ...initial });
  const [saving, setSaving] = useState(false);

  const setLatLng = ({ latitude, longitude }) => setForm((f) => ({ ...f, latitude, longitude }));

  const applyResolved = (a) => {
    setForm((f) => ({
      ...f,
      line1: [a.house_number, a.road].filter(Boolean).join(" ") || f.line1,
      line2: a.suburb || a.neighbourhood || a.residential || f.line2,
      city: a.city || a.town || a.village || a.county || f.city,
      state: a.state || f.state,
      pincode: a.postcode || f.pincode,
      country: a.country || "India",
      place_name: a.display_name || f.place_name,
      latitude: a.latitude ?? f.latitude,
      longitude: a.longitude ?? f.longitude,
    }));
  };

  const save = async () => {
    if (!form.name || !form.phone || !form.line1 || !form.city || !form.state || !form.pincode) {
      return toast.error("Please complete all required fields");
    }
    if (!form.latitude || !form.longitude) {
      return toast.error("Please pinpoint your exact location on the map");
    }
    setSaving(true);
    try {
      const { data } = await api.post("/addresses", form);
      toast.success("Address saved");
      onSaved && onSaved({ ...form, id: data.id });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save address");
    }
    setSaving(false);
  };

  return (
    <div className="border hairline p-6 bg-cream-50 space-y-4" data-testid="address-form">
      <div className="eyebrow">Delivery address</div>

      <MapPicker
        value={{ latitude: form.latitude, longitude: form.longitude }}
        onChange={setLatLng}
        onAddressResolved={applyResolved}
      />

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Label</Label>
          <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="rounded-none" data-testid="addr-label" />
        </div>
        <div>
          <Label>Full name*</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" data-testid="addr-name" />
        </div>
        <div className="col-span-2">
          <Label>Phone*</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none" data-testid="addr-phone" />
        </div>
        <div className="col-span-2">
          <Label>House / Flat / Building*</Label>
          <Input value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} className="rounded-none" data-testid="addr-line1" />
        </div>
        <div className="col-span-2">
          <Label>Area / Locality (optional)</Label>
          <Input value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })} className="rounded-none" />
        </div>
        <div>
          <Label>City*</Label>
          <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-none" data-testid="addr-city" />
        </div>
        <div>
          <Label>State*</Label>
          <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="rounded-none" data-testid="addr-state" />
        </div>
        <div>
          <Label>Pincode*</Label>
          <Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} className="rounded-none" data-testid="addr-pincode" />
        </div>
        <div>
          <Label>Country</Label>
          <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="rounded-none" />
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} data-testid="addr-default" />
        <span>Make this my default address</span>
      </label>

      <div className="flex gap-3 pt-2">
        <button onClick={save} disabled={saving} className="btn-primary-ink px-6 py-3 text-xs uppercase tracking-widest disabled:opacity-50" data-testid="save-addr-btn">
          {saving ? "Saving…" : "Save address"}
        </button>
        {onCancel && (
          <button onClick={onCancel} className="text-xs uppercase tracking-widest text-ink-500 hover:text-ink-900" data-testid="cancel-addr-btn">
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
